import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { getArticlePublic } from "@/lib/content.functions";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const { article } = await getArticlePublic({ data: { slug: params.slug } });
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Article — Bosque Suspendido" }] };
    const url = `https://bosque-suspendido.lovable.app/blog/${params.slug}`;
    return {
      meta: [
        { title: `${a.title} | Bosque Suspendido` },
        { name: "description", content: a.excerpt },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(a.cover_url ? [{ property: "og:image", content: a.cover_url }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: a.title,
            description: a.excerpt,
            author: { "@type": "Person", name: a.author },
            datePublished: a.published_at ?? undefined,
            image: a.cover_url ?? undefined,
            mainEntityOfPage: url,
            publisher: {
              "@type": "Organization",
              name: "Bosque Suspendido",
              logo: {
                "@type": "ImageObject",
                url: "https://bosque-suspendido.lovable.app/favicon-32x32.png",
              },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Article not found</h1>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 mt-8 text-gold text-xs tracking-[0.25em] uppercase"
        >
          <ArrowLeft size={14} /> Back to blog
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Something went wrong</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
      </div>
    </Layout>
  ),
  component: ArticlePage,
});

function formatDate(iso: string | null, locale: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function ArticlePage() {
  const { article } = Route.useLoaderData();
  const { tk, lang } = useT();
  const locale = lang === "pt" ? "pt-PT" : lang === "en" ? "en-GB" : "es-ES";

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-6 pt-20 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-foreground/60 hover:text-gold"
        >
          <ArrowLeft size={14} /> {tk("diario.detail.800602", "Blog")}
        </Link>

        <span
          className={`inline-block mt-8 text-[10px] tracking-[0.3em] uppercase px-3 py-1 rounded-full border ${
            article.category === "noticias"
              ? "border-gold/60 text-gold"
              : "border-foreground/30 text-foreground/70"
          }`}
        >
          {article.category === "noticias" ? tk("diario.detail.f8f25f", "News") : tk("diario.detail.0b9d2b", "Blog")}
        </span>

        <h1 className="font-serif text-4xl md:text-5xl mt-5 leading-tight">{article.title}</h1>

        <div className="mt-6 flex flex-wrap gap-5 text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
          <span className="flex items-center gap-2">
            <Calendar size={12} className="text-gold" />
            {formatDate(article.published_at, locale)}
          </span>
          <span className="flex items-center gap-2">
            <Clock size={12} className="text-gold" />
            {article.reading_time}
          </span>
          <span>{tk("diario.detail.5e27d8", "By")} {article.author}</span>
        </div>

        {article.cover_url && (
          <img
            src={article.cover_url}
            alt={article.title}
            className="mt-10 rounded-2xl border border-gold/15 w-full"
          />
        )}

        <div className="mt-12 space-y-6 text-foreground/85 leading-relaxed">
          {article.content.map((p: string, i: number) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-16 p-8 rounded-2xl border border-gold/30 bg-card/40">
          <h3 className="font-serif text-2xl">{tk("diario.detail.eb14e4", "Have a project in mind?")}</h3>
          <p className="mt-3 text-sm text-muted-foreground">
            {tk("diario.detail.7d8855", "Book a 45-min discovery call with our team and we'll scope it together.")}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 mt-6 rounded-full border border-gold text-gold text-xs tracking-[0.25em] uppercase px-6 py-2.5 hover:bg-gold hover:text-primary-foreground transition-colors"
          >
            {tk("diario.detail.2a4517", "Get in touch")} <ArrowRight size={14} />
          </Link>
        </div>
      </article>
    </Layout>
  );
}
